import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { User, Patient, Encounter, Notification, Role } from '../types';
import { db } from '../config/firebase';
import { collection, doc, setDoc, deleteDoc, onSnapshot, writeBatch, query, orderBy } from 'firebase/firestore';
interface DataContextType {
  users: User[];
  patients: Patient[];
  notifications: Notification[];
  addUser: (user: User) => void;
  updateUser: (user: User) => void;
  deleteUser: (id: string) => void;
  addPatient: (patient: Patient) => void;
  updatePatient: (patient: Patient) => void;
  addEncounter: (patientId: string, encounter: Encounter) => void;
  updateEncounter: (patientId: string, encounter: Encounter) => void;
  addNotification: (notification: Omit<Notification, 'id' | 'date' | 'read'>) => void;
  markNotificationRead: (id: string) => void;
  generateFileNo: () => string;
  isOnline: boolean;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
const defaultUsers: User[] = [{
  id: '1',
  name: 'Admin',
  role: 'Administrator',
  password: 'Admin@123'
}, {
  id: '2',
  name: 'Mwamba Chilufya',
  role: 'Reception',
  password: 'Password@123'
}, {
  id: '3',
  name: 'Bwalya Mutale',
  role: 'Nursing',
  password: 'Password@123'
}, {
  id: '4',
  name: 'Dr. Chanda Kapasa',
  role: 'Clinician',
  password: 'Password@123'
}, {
  id: '5',
  name: 'Mulenga Banda',
  role: 'Laboratory',
  password: 'Password@123'
}, {
  id: '6',
  name: 'Kabwe Phiri',
  role: 'Radiology',
  password: 'Password@123'
}, {
  id: '7',
  name: 'Lungu Tembo',
  role: 'Pharmacy',
  password: 'Password@123'
}];
const defaultPatients: Patient[] = [{
  id: 'p1',
  personalDetails: {
    fileNo: 'BMC_001/26',
    date: new Date().toISOString().split('T')[0],
    fullNames: 'Mumba Kalusa',
    sex: 'Male',
    age: '45',
    maritalStatus: 'Married',
    homeAddress: 'Plot 123, Kabulonga, Lusaka',
    contact: '0977123456',
    occupation: 'Teacher',
    nextOfKin: {
      name: 'Sarah Kalusa',
      contact: '0966123456',
      relationship: 'Wife'
    },
    allergies: 'Penicillin',
    hivStatus: 'Negative'
  },
  encounters: []
}];
// Check if Firebase is configured (not using placeholder values)
const isFirebaseConfigured = (): boolean => {
  try {
    // If db was created successfully and the app has a real project ID
    const projectId = db.app.options.projectId;
    return !!projectId && projectId !== 'YOUR_PROJECT_ID';
  } catch {
    return false;
  }
};
export const DataProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const firebaseEnabled = useRef(isFirebaseConfigured());
  const [isOnline, setIsOnline] = useState(firebaseEnabled.current);
  const [users, setUsers] = useState<User[]>(() => {
    const saved = localStorage.getItem('bmed_users');
    return saved ? JSON.parse(saved) : defaultUsers;
  });
  const [patients, setPatients] = useState<Patient[]>(() => {
    const saved = localStorage.getItem('bmed_patients');
    return saved ? JSON.parse(saved) : defaultPatients;
  });
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    const saved = localStorage.getItem('bmed_notifications');
    return saved ? JSON.parse(saved) : [];
  });
  // Track whether initial Firestore data has loaded
  const [firestoreReady, setFirestoreReady] = useState(!firebaseEnabled.current);
  const suppressLocalSync = useRef(false);
  // ─── FIRESTORE REAL-TIME LISTENERS ───
  useEffect(() => {
    if (!firebaseEnabled.current) return;
    const unsubUsers = onSnapshot(collection(db, 'users'), snapshot => {
      if (snapshot.empty && !firestoreReady) {
        // Seed Firestore with default users on first run
        const batch = writeBatch(db);
        defaultUsers.forEach(u => {
          batch.set(doc(db, 'users', u.id), u);
        });
        batch.commit().catch(console.error);
        return;
      }
      const data = snapshot.docs.map(d => d.data() as User);
      suppressLocalSync.current = true;
      setUsers(data);
      localStorage.setItem('bmed_users', JSON.stringify(data));
      setTimeout(() => {
        suppressLocalSync.current = false;
      }, 50);
    }, error => {
      console.error('Firestore users listener error:', error);
      setIsOnline(false);
    });
    const unsubPatients = onSnapshot(collection(db, 'patients'), snapshot => {
      if (snapshot.empty && !firestoreReady) {
        // Seed Firestore with default patient on first run
        const batch = writeBatch(db);
        defaultPatients.forEach(p => {
          batch.set(doc(db, 'patients', p.id), p);
        });
        batch.commit().catch(console.error);
        return;
      }
      const data = snapshot.docs.map(d => d.data() as Patient);
      suppressLocalSync.current = true;
      setPatients(data);
      localStorage.setItem('bmed_patients', JSON.stringify(data));
      setTimeout(() => {
        suppressLocalSync.current = false;
      }, 50);
    }, error => {
      console.error('Firestore patients listener error:', error);
      setIsOnline(false);
    });
    const unsubNotifications = onSnapshot(collection(db, 'notifications'), snapshot => {
      const data = snapshot.docs.map(d => d.data() as Notification).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      suppressLocalSync.current = true;
      setNotifications(data);
      localStorage.setItem('bmed_notifications', JSON.stringify(data));
      setTimeout(() => {
        suppressLocalSync.current = false;
      }, 50);
      if (!firestoreReady) setFirestoreReady(true);
    }, error => {
      console.error('Firestore notifications listener error:', error);
      setIsOnline(false);
      if (!firestoreReady) setFirestoreReady(true);
    });
    setIsOnline(true);
    return () => {
      unsubUsers();
      unsubPatients();
      unsubNotifications();
    };
  }, []);
  // ─── LOCAL STORAGE FALLBACK (when Firebase is not configured) ───
  useEffect(() => {
    if (firebaseEnabled.current && suppressLocalSync.current) return;
    localStorage.setItem('bmed_users', JSON.stringify(users));
  }, [users]);
  useEffect(() => {
    if (firebaseEnabled.current && suppressLocalSync.current) return;
    localStorage.setItem('bmed_patients', JSON.stringify(patients));
  }, [patients]);
  useEffect(() => {
    if (firebaseEnabled.current && suppressLocalSync.current) return;
    localStorage.setItem('bmed_notifications', JSON.stringify(notifications));
  }, [notifications]);
  // Cross-tab sync (still useful as backup, especially when Firebase is off)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (!e.key || !e.newValue) return;
      if (firebaseEnabled.current) return; // Firebase handles sync
      try {
        if (e.key === 'bmed_users') setUsers(JSON.parse(e.newValue));else if (e.key === 'bmed_patients') setPatients(JSON.parse(e.newValue));else if (e.key === 'bmed_notifications') setNotifications(JSON.parse(e.newValue));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  // ─── FIRESTORE WRITE HELPERS ───
  const writeToFirestore = useCallback(async (collectionName: string, id: string, data: any) => {
    if (!firebaseEnabled.current) return;
    try {
      await setDoc(doc(db, collectionName, id), data);
    } catch (error) {
      console.error(`Error writing to ${collectionName}:`, error);
    }
  }, []);
  const deleteFromFirestore = useCallback(async (collectionName: string, id: string) => {
    if (!firebaseEnabled.current) return;
    try {
      await deleteDoc(doc(db, collectionName, id));
    } catch (error) {
      console.error(`Error deleting from ${collectionName}:`, error);
    }
  }, []);
  // ─── DATA OPERATIONS ───
  const addUser = (user: User) => {
    setUsers(prev => [...prev, user]);
    writeToFirestore('users', user.id, user);
  };
  const updateUser = (user: User) => {
    setUsers(prev => prev.map(u => u.id === user.id ? user : u));
    writeToFirestore('users', user.id, user);
  };
  const deleteUser = (id: string) => {
    setUsers(prev => prev.filter(u => u.id !== id));
    deleteFromFirestore('users', id);
  };
  const addPatient = (patient: Patient) => {
    setPatients(prev => [...prev, patient]);
    writeToFirestore('patients', patient.id, patient);
  };
  const updatePatient = (patient: Patient) => {
    setPatients(prev => prev.map(p => p.id === patient.id ? patient : p));
    writeToFirestore('patients', patient.id, patient);
  };
  const addEncounter = (patientId: string, encounter: Encounter) => {
    setPatients(prev => {
      const updated = prev.map(p => {
        if (p.id === patientId) {
          const updatedPatient = {
            ...p,
            encounters: [...p.encounters, encounter]
          };
          writeToFirestore('patients', patientId, updatedPatient);
          return updatedPatient;
        }
        return p;
      });
      return updated;
    });
  };
  const updateEncounter = (patientId: string, encounter: Encounter) => {
    setPatients(prev => {
      const updated = prev.map(p => {
        if (p.id === patientId) {
          const updatedPatient = {
            ...p,
            encounters: p.encounters.map(e => e.id === encounter.id ? encounter : e)
          };
          writeToFirestore('patients', patientId, updatedPatient);
          return updatedPatient;
        }
        return p;
      });
      return updated;
    });
  };
  const addNotification = (notif: Omit<Notification, 'id' | 'date' | 'read'>) => {
    const newNotif: Notification = {
      ...notif,
      id: Date.now().toString(),
      date: new Date().toISOString(),
      read: false
    };
    setNotifications(prev => [newNotif, ...prev]);
    writeToFirestore('notifications', newNotif.id, newNotif);
  };
  const markNotificationRead = (id: string) => {
    setNotifications(prev => {
      const updated = prev.map(n => n.id === id ? {
        ...n,
        read: true
      } : n);
      const notif = updated.find(n => n.id === id);
      if (notif) writeToFirestore('notifications', id, notif);
      return updated;
    });
  };
  const generateFileNo = () => {
    const year = new Date().getFullYear().toString().slice(-2);
    const count = patients.length + 1;
    return `BMC_${count.toString().padStart(3, '0')}/${year}`;
  };
  return <DataContext.Provider value={{
    users,
    patients,
    notifications,
    addUser,
    updateUser,
    deleteUser,
    addPatient,
    updatePatient,
    addEncounter,
    updateEncounter,
    addNotification,
    markNotificationRead,
    generateFileNo,
    isOnline
  }} data-id="element-75">
      {children}
    </DataContext.Provider>;
};
export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) throw new Error('useData must be used within a DataProvider');
  return context;
};